import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Bot, Network, X, ExternalLink, Copy, Check } from "lucide-react";
import { toast } from "sonner";

// Artifact Vault Card — uses the same brutalist TCG framing as VaultCard.jsx
// but renders from artifact-card data (kind, ref, rarity, accent, name, etc.)
// Click opens an ArtifactCardDetailModal — and a small "View File" link below
// the card takes the user to the underlying archive entry.

const RARITY_COLOR = {
  rare:       "#22d3ee",
  mythic:     "#a78bfa",
  classified: "#ff3b30",
};

const RARITY_LABEL = {
  rare:       "RARE",
  mythic:     "MYTHIC",
  classified: "CLASSIFIED",
};

const KIND_ICON = {
  dossier:    FileText,
  ai_dossier: Bot,
  connection: Network,
};

const KIND_LABEL = {
  dossier:    "DOSSIER",
  ai_dossier: "DEEP DIVE",
  connection: "CONNECTION",
};

const KIND_ROUTE = {
  dossier:    (ref) => `/dossier/${ref}`,
  ai_dossier: (ref) => `/ai-dossier/${ref}`,
  connection: (ref) => `/connection/${ref}`,
};

const SIZES = {
  sm: { card: "w-[160px] h-[224px]", iconBoxPx: 96,  nameClass: "text-sm",  flavorClass: "text-[10px] line-clamp-2" },
  md: { card: "w-[200px] h-[280px]", iconBoxPx: 128, nameClass: "text-base", flavorClass: "text-[10px] line-clamp-3" },
  lg: { card: "w-[260px] h-[364px]", iconBoxPx: 176, nameClass: "text-lg",   flavorClass: "text-[11px] line-clamp-4" },
};

/**
 * ArtifactVaultCard — click opens a detail modal that shows the full-size
 * Gemini art plus all card metadata. A separate "View source file" link in
 * the modal takes the user to the underlying dossier / dive / connection.
 */
export default function ArtifactVaultCard({ card, size = "md", testid }) {
  const [detailOpen, setDetailOpen] = useState(false);
  const rarity = card?.rarity || "rare";
  const color = card?.accent || RARITY_COLOR[rarity] || "#22d3ee";
  const rarityLabel = RARITY_LABEL[rarity] || rarity.toUpperCase();
  const isClassified = rarity === "classified";
  const sizes = SIZES[size] || SIZES.md;
  const KindIcon = KIND_ICON[card?.kind] || FileText;
  const kindLabel = KIND_LABEL[card?.kind] || "ARTIFACT";
  const title = card?.name || card?.title || "Untitled";
  const flavor = card?.flavor || (card?.subjects?.length ? `// ${card.subjects.slice(0, 3).join(" · ")}` : "");
  const artUrl = card?.art_url
    ? `${process.env.REACT_APP_BACKEND_URL || ""}${card.art_url}`
    : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setDetailOpen(true)}
        data-testid={testid || `artifact-vault-card-${card?.id}`}
        data-rarity={rarity}
        className={`relative ${sizes.card} bg-[#0a0a0a] border flex flex-col overflow-hidden transition-all duration-200 cursor-pointer hover:translate-y-[-2px] text-left ${isClassified ? "vc-classified-shimmer vc-classified-glow" : ""}`}
        style={{ borderColor: isClassified ? color : `${color}55` }}
      >
        {/* TOP RARITY STRIPE */}
        <div className="h-[3px] w-full shrink-0" style={{ backgroundColor: color }} />

        {/* CATEGORY ROW */}
        <div
          className="px-2.5 py-1.5 flex items-center justify-between font-mono text-[9px] tracking-[0.15em] relative z-[3]"
          style={{ borderBottom: `1px solid ${color}22` }}
        >
          <span className="text-zinc-500 truncate inline-flex items-center gap-1.5">
            <KindIcon size={9} /> {kindLabel}
          </span>
          <span className="font-bold shrink-0 ml-1" style={{ color }}>{rarityLabel}</span>
        </div>

        {/* ARTWORK */}
        <div className="relative flex-shrink-0 overflow-hidden" style={{ height: sizes.iconBoxPx }}>
          {artUrl ? (
            <img
              src={artUrl}
              alt={title}
              loading="lazy"
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : null}
          <div
            className="absolute inset-0 -z-[1] flex items-center justify-center"
            style={{
              background: `radial-gradient(circle at 50% 45%, ${color}33 0%, transparent 65%), repeating-linear-gradient(45deg, ${color}06 0 6px, transparent 6px 12px)`,
            }}
          >
            <KindIcon size={Math.round(sizes.iconBoxPx * 0.4)} color={color} strokeWidth={1.2} opacity={0.18} />
          </div>
          {card?.edition != null && (
            <div
              className="absolute top-1 right-1 font-mono text-[8px] tracking-[0.2em] px-1.5 py-0.5 border bg-[#050505]/80 z-[3]"
              style={{ borderColor: `${color}66`, color }}
            >
              #{card.edition}
            </div>
          )}
        </div>

        {/* NAME */}
        <div className="px-2.5 pt-2 pb-1 relative z-[3]">
          <div className={`font-heading font-black uppercase tracking-tight ${sizes.nameClass} leading-tight text-white line-clamp-2`}>
            {title}
          </div>
        </div>

        {/* FLAVOR */}
        <div className={`px-2.5 ${sizes.flavorClass} text-zinc-400 flex-1 relative z-[3]`}>
          {flavor}
        </div>

        {/* BOTTOM RARITY STRIPE */}
        <div className="h-[3px] w-full shrink-0" style={{ backgroundColor: color }} />
      </button>

      {detailOpen && (
        <ArtifactCardDetailModal card={card} onClose={() => setDetailOpen(false)} />
      )}
    </>
  );
}

/**
 * ArtifactCardDetailModal — full-card lightbox.
 * Shows the Gemini art at full size, every metadata field, an "Open source
 * file" link, and a copy-link helper for sharing the card.
 */
function ArtifactCardDetailModal({ card, onClose }) {
  const rarity = card?.rarity || "rare";
  const color = card?.accent || RARITY_COLOR[rarity] || "#22d3ee";
  const rarityLabel = RARITY_LABEL[rarity] || rarity.toUpperCase();
  const isClassified = rarity === "classified";
  const KindIcon = KIND_ICON[card?.kind] || FileText;
  const kindLabel = KIND_LABEL[card?.kind] || "ARTIFACT";
  const sourceUrl = (KIND_ROUTE[card?.kind] || ((r) => `#`))(card?.ref);
  const title = card?.name || card?.title || "Untitled";
  const flavor = card?.flavor || (card?.subjects?.length ? `// ${card.subjects.slice(0, 4).join(" · ")}` : "");
  const artUrl = card?.art_url
    ? `${process.env.REACT_APP_BACKEND_URL || ""}${card.art_url}`
    : null;

  const [copied, setCopied] = useState(false);
  const copyLink = async () => {
    const url = `${window.location.origin}${sourceUrl}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  // Esc-to-close
  React.useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[110] bg-[#050505]/95 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="artifact-card-detail-modal"
      onClick={onClose}
    >
      <div
        className={`relative w-full max-w-[420px] max-h-[92vh] bg-[#0a0a0a] border flex flex-col overflow-y-auto overflow-x-hidden ${isClassified ? "vc-classified-shimmer" : ""}`}
        style={{ borderColor: isClassified ? color : `${color}aa` }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="artifact-card-detail-close"
          className="absolute top-2 right-2 z-[5] w-7 h-7 flex items-center justify-center bg-[#050505]/85 border border-white/20 text-zinc-300 hover:text-white hover:border-white/50"
        >
          <X size={13} />
        </button>

        <div className="h-[3px] w-full shrink-0" style={{ backgroundColor: color }} />

        <div className="px-4 py-2 flex items-center justify-between font-mono text-[10px] tracking-[0.25em] shrink-0" style={{ borderBottom: `1px solid ${color}33` }}>
          <span className="text-zinc-500 inline-flex items-center gap-2">
            <KindIcon size={10} /> {kindLabel}
          </span>
          <span className="font-bold" style={{ color }}>{rarityLabel}</span>
        </div>

        {/* Artwork — capped at 360px tall on small viewports so the whole card fits */}
        <div className="relative w-full shrink-0" style={{ aspectRatio: "1 / 1", maxHeight: "min(360px, 50vh)" }}>
          {artUrl ? (
            <img
              src={artUrl}
              alt={title}
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : null}
          <div
            className="absolute inset-0 -z-[1] flex items-center justify-center"
            style={{
              background: `radial-gradient(circle at 50% 45%, ${color}33 0%, transparent 65%), repeating-linear-gradient(45deg, ${color}06 0 6px, transparent 6px 12px)`,
            }}
          >
            <KindIcon size={96} color={color} strokeWidth={1.2} opacity={0.18} />
          </div>
          {card?.edition != null && (
            <div
              className="absolute top-2 right-2 font-mono text-[9px] tracking-[0.2em] px-1.5 py-0.5 border bg-[#050505]/80"
              style={{ borderColor: `${color}99`, color }}
            >
              #{card.edition}
            </div>
          )}
        </div>

        <div className="px-4 py-3">
          <div className="font-heading font-black uppercase tracking-tight text-lg text-white leading-tight">
            {title}
          </div>
          {flavor && (
            <div className="font-mono text-[10px] text-zinc-400 mt-1.5 leading-relaxed">
              {flavor}
            </div>
          )}
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            {card?.minted_at && (
              <span className="font-mono text-[9px] tracking-[0.2em] text-zinc-500">
                MINTED · {new Date(card.minted_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
              </span>
            )}
            {card?.owner_username && (
              <span className="font-mono text-[9px] tracking-[0.2em] text-zinc-500">
                HOLDER · <Link to={`/u/${card.owner_username}`} className="text-white hover:underline">/u/{card.owner_username}</Link>
              </span>
            )}
          </div>
        </div>

        <div className="px-4 pb-3 flex flex-wrap gap-2 border-t border-white/10 pt-2.5">
          <Link
            to={sourceUrl}
            data-testid="artifact-card-detail-view-source"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/15 text-zinc-300 hover:text-white hover:border-white/40 font-mono text-[10px] uppercase tracking-[0.2em]"
            onClick={onClose}
          >
            <ExternalLink size={10} /> View Source File
          </Link>
          <button
            type="button"
            onClick={copyLink}
            data-testid="artifact-card-detail-copy-link"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/15 text-zinc-300 hover:text-white hover:border-white/40 font-mono text-[10px] uppercase tracking-[0.2em]"
          >
            {copied ? <Check size={10} /> : <Copy size={10} />} {copied ? "Copied" : "Copy Link"}
          </button>
        </div>

        <div className="h-[3px] w-full shrink-0" style={{ backgroundColor: color }} />
      </div>
    </div>
  );
}
